"use client"

import { ControlledModal, toast } from "@/components"
import { Button, Text, TextInput, Toggle } from "@/elements"
import { FragmentType, graphql, useFragment } from "@/gql"
import {
  CreativePublicVisibility,
  UpdateCreativeWebProfileHandleDocument,
  UpdateCreativeWebProfilePublicVisibilityDocument,
} from "@/gql/graphql"
import { useSession } from "@/hooks/useSession"
import { copyToClipboard } from "@/utils/copyToClipboard"
import { useMutation } from "@apollo/client"
import { LinkIcon } from "@heroicons/react/24/outline"
import { captureException } from "@sentry/nextjs"
import clsx from "clsx"
import { useRouter } from "next-nprogress-bar"
import { FC, useEffect, useState } from "react"

const ProfileVisibilityModalFragment = graphql(`
  fragment ProfileVisibilityModal on IndividualCreative {
    id
    __typename
    handle
    publicVisibility
  }
`)

interface Props {
  isOpen: boolean
  onOpenChange: (isOpen: boolean) => void
  creative: FragmentType<typeof ProfileVisibilityModalFragment> | null | undefined
}

graphql(`
  mutation UpdateCreativeWebProfilePublicVisibility($input: UpdateCreativeWebProfilePublicVisibilityInput!) {
    updateCreativeWebProfilePublicVisibility(input: $input) {
      errors {
        field
        messages
      }
      creative {
        __typename
        id
        publicVisibility
      }
    }
  }
`)

graphql(`
  mutation UpdateCreativeWebProfileHandle($input: UpdateCreativeWebProfileHandleInput!) {
    updateCreativeWebProfileHandle(input: $input) {
      errors {
        field
        messages
      }
      creative {
        __typename
        id
        handle
      }
    }
  }
`)

const handlePrefix = "itsnova.com/"

export const ProfileVisibilityModal: FC<Props> = ({ isOpen, onOpenChange, ...props }) => {
  const [updateVisibility] = useMutation(UpdateCreativeWebProfilePublicVisibilityDocument)
  const [updateHandle] = useMutation(UpdateCreativeWebProfileHandleDocument)
  const creative = useFragment(ProfileVisibilityModalFragment, props.creative)
  const [visibilityValue, setVisibilityValue] = useState<CreativePublicVisibility>(
    creative?.publicVisibility || CreativePublicVisibility.Private
  )
  const { update: updateSession, data: session } = useSession()

  const [handleValue, setHandleValue] = useState(creative?.handle || "")
  const [isLoading, setIsLoading] = useState(false)
  const router = useRouter()

  useEffect(() => {
    if (creative) {
      setHandleValue(creative?.handle || "")
      setVisibilityValue(creative?.publicVisibility || CreativePublicVisibility.Private)
    }
  }, [creative])

  const onUpdate = async () => {
    setIsLoading(true)
    const errors = []

    if (visibilityValue !== creative?.publicVisibility) {
      const visResponse = await updateVisibility({
        variables: {
          input: {
            id: creative?.id!,
            publicVisibility: visibilityValue,
          },
        },
      })

      const visErrors = visResponse?.data?.updateCreativeWebProfilePublicVisibility?.errors
      if (visErrors && visErrors.length > 0) {
        captureException(visErrors)
        errors.push(...visErrors)
        toast.error("Error updating visibility")
      }
    }

    if (handleValue !== creative?.handle) {
      const handleResponse = await updateHandle({
        variables: {
          input: {
            id: creative?.id!,
            handle: handleValue.replace(handlePrefix, ""),
          },
        },
      })

      const handleErrors = handleResponse?.data?.updateCreativeWebProfileHandle?.errors
      if (handleErrors && handleErrors.length > 0) {
        const handleAlreadyTaken = handleErrors.some(
          error => error.field === "handle" && error.messages.includes("This handle is already taken.")
        )

        errors.push(...handleErrors)

        if (handleAlreadyTaken) {
          toast.error("This handle is already taken.")
        } else {
          captureException(handleErrors)
          toast.error("Error updating handle")
        }
      } else {
        // route to new handle
        await updateSession({
          ...session,
          user: { ...session?.user, creative: { ...session?.user?.creative, handle: handleValue } },
        })
        window.location.href = `/${handleValue.replace(handlePrefix, "")}?edit=true`
      }
    }
    setIsLoading(false)

    if (errors && errors.length > 0) {
      console.error("Error updating roles", errors)
    } else {
      onOpenChange(false)
    }
  }

  const isPrivate = visibilityValue === CreativePublicVisibility.Private

  const leftElement = () => <LinkIcon className="size-[20px] stroke-fg-1" />

  return (
    <ControlledModal
      open={isOpen}
      onOpenChange={onOpenChange}
      header={{ title: "Profile settings", variant: "default", fixed: true }}
      body={
        <div className="mb-12 flex flex-1 flex-col">
          <div className={clsx("flex flex-row gap-5", isPrivate ? "text-red-dark" : "text-fg-1")}>
            <Text>
              <Text element="span" className={clsx(isPrivate ? "text-red-dark" : "text-fg-2")}>
                Visibility:{" "}
              </Text>
              {isPrivate ? "Private" : "Public"}
            </Text>
            <Toggle
              checked={!isPrivate}
              onChange={() => {
                setVisibilityValue(isPrivate ? CreativePublicVisibility.Public : CreativePublicVisibility.Private)
              }}
            />
          </div>
          <Text className="mt-3 text-fg-2" variant="body2">
            Private portfolios are only viewable to authenticated NOVA users. Public portfolios are accessible to anyone
            on the web.
          </Text>

          {!isPrivate && (
            <div>
              <div className="py-6">
                <TextInput
                  label="Portfolio URL"
                  tabIndex={0}
                  value={handlePrefix + handleValue.replace(handlePrefix, "")}
                  onChange={e => {
                    if (e.target.value.length >= handlePrefix.length) {
                      const value = e.target.value.replace(handlePrefix, "")

                      setHandleValue(value)
                    }
                  }}
                />
              </div>
              <div>
                <Button
                  LeftElement={leftElement}
                  variant="tertiary"
                  onClick={() => copyToClipboard(handlePrefix + handleValue.replace(handlePrefix, ""))}>
                  Copy web portfolio link
                </Button>
              </div>
            </div>
          )}
        </div>
      }
      footer={{
        fixed: true,
        tertiaryButton: {
          children: "Cancel",
          onClick: () => onOpenChange(false),
        },
        primaryButton: {
          children: "Update",
          onClick: onUpdate,
          loading: isLoading,
        },
      }}
    />
  )
}
